import { useState, useEffect, useRef } from "react";
import { SocialIconsRow } from "./SocialIcons";

const TreasureChestIcon = ({ sparkleDelay = 0, unlocked = false }) => (
  <span
    className="treasure-lock-icon"
    style={{ "--sparkle-delay": `${sparkleDelay}s` }}
  >
    {unlocked ? (
        <svg viewBox="0 0 64 64" className="treasure-chest-svg" xmlns="http://www.w3.org/2000/svg">
          <rect x="8" y="35" width="48" height="23" rx="4" fill="#8B5E3C" stroke="#5C3A1E" strokeWidth="2"/>
          <rect x="8" y="35" width="48" height="8" rx="4" fill="#A0724A"/>
          <rect x="12" y="37" width="4" height="19" rx="1" fill="#6B4226"/>
          <rect x="48" y="37" width="4" height="19" rx="1" fill="#6B4226"/>
          <path d="M8 35 L8 20 Q32 8 56 20 L56 35" fill="#A0724A" stroke="#5C3A1E" strokeWidth="2"/>
          <path d="M8 20 Q32 8 56 20" fill="none" stroke="#6B4226" strokeWidth="1.5"/>
          <rect x="26" y="16" width="12" height="8" rx="2" fill="#FFD700" stroke="#B8860B" strokeWidth="1.5"
                transform="rotate(-15 32 20)"/>
          <circle cx="32" cy="20" r="1.5" fill="#5C3A1E" transform="rotate(-15 32 20)"/>
          <circle cx="20" cy="32" r="4" fill="#FFD700" stroke="#DAA520" strokeWidth="1"/>
          <text x="20" y="34" fontSize="5" fill="#B8860B" textAnchor="middle" fontWeight="bold">$</text>
          <circle cx="28" cy="28" r="3.5" fill="#FFD700" stroke="#DAA520" strokeWidth="1"/>
          <text x="28" y="30" fontSize="4.5" fill="#B8860B" textAnchor="middle" fontWeight="bold">$</text>
          <circle cx="36" cy="30" r="4" fill="#FFD700" stroke="#DAA520" strokeWidth="1"/>
          <text x="36" y="32" fontSize="5" fill="#B8860B" textAnchor="middle" fontWeight="bold">$</text>
          <circle cx="44" cy="34" r="3.5" fill="#FFD700" stroke="#DAA520" strokeWidth="1"/>
          <text x="44" y="36" fontSize="4.5" fill="#B8860B" textAnchor="middle" fontWeight="bold">$</text>
        </svg>
    ) : (
      <div>
        <span className="treasure-sparkle treasure-sparkle-1" />
        <span className="treasure-sparkle treasure-sparkle-2" />
        <span className="treasure-sparkle treasure-sparkle-3" />
        <svg viewBox="0 0 64 64" className="treasure-chest-svg" xmlns="http://www.w3.org/2000/svg">
          <rect x="8" y="28" width="48" height="30" rx="4" fill="#8B5E3C" stroke="#5C3A1E" strokeWidth="2"/>
          <rect x="8" y="28" width="48" height="12" rx="4" fill="#A0724A"/>
          <path d="M8 32 Q32 22 56 32" fill="#6B4226" stroke="#5C3A1E" strokeWidth="1.5"/>
          <rect x="26" y="32" width="12" height="10" rx="2" fill="#FFD700" stroke="#B8860B" strokeWidth="1.5"/>
          <circle cx="32" cy="38" r="2" fill="#5C3A1E"/>
          <rect x="12" y="30" width="4" height="24" rx="1" fill="#6B4226"/>
          <rect x="48" y="30" width="4" height="24" rx="1" fill="#6B4226"/>
        </svg>
      </div>
    )}
  </span>
);

const popperLeftPieces = [
  { dx: 180, dy: -120, rot: 540, w: 9, h: 14, c: "#fbed65", delay: 0.15 },
  { dx: 230, dy: -60, rot: 420, w: 10, h: 6, c: "#2ecc71", delay: 0.18 },
  { dx: 270, dy: -150, rot: 720, w: 7, h: 12, c: "#ff6b6b", delay: 0.2 },
  { dx: 130, dy: -190, rot: 380, w: 8, h: 8, c: "#4d9be6", delay: 0.16 },
  { dx: 300, dy: -90, rot: 600, w: 6, h: 10, c: "#ffffff", delay: 0.22 },
  { dx: 200, dy: -220, rot: 320, w: 10, h: 10, c: "#fbed65", delay: 0.19 },
  { dx: 160, dy: -50, rot: 480, w: 8, h: 14, c: "#ff8c42", delay: 0.17 },
  { dx: 260, dy: -180, rot: 540, w: 7, h: 7, c: "#2ecc71", delay: 0.21 },
];

const popperRightPieces = popperLeftPieces.map((p, i) => ({
  ...p,
  dx: -p.dx,
  rot: -p.rot,
  delay: 0.4 + (i * 0.015),
}));

const popperMidPieces = [
  { dx: -150, dy: -210, rot: 360, w: 9, h: 14, c: "#fbed65", delay: 0.65 },
  { dx: 140, dy: -220, rot: -360, w: 8, h: 10, c: "#2ecc71", delay: 0.66 },
  { dx: -220, dy: -150, rot: 540, w: 8, h: 8, c: "#ff6b6b", delay: 0.68 },
  { dx: 230, dy: -130, rot: -540, w: 7, h: 12, c: "#4d9be6", delay: 0.7 },
  { dx: -70, dy: -250, rot: 420, w: 10, h: 6, c: "#fbed65", delay: 0.67 },
  { dx: 90, dy: -260, rot: -420, w: 8, h: 8, c: "#ffffff", delay: 0.72 },
  { dx: -180, dy: -80, rot: 600, w: 9, h: 12, c: "#ff8c42", delay: 0.74 },
  { dx: 200, dy: -70, rot: -600, w: 7, h: 14, c: "#2ecc71", delay: 0.69 },
];

const renderPopper = (pieces, className) => (
  <div className={className} aria-hidden="true">
    {pieces.map((p, i) => (
      <span
        key={i}
        className="popper-piece"
        style={{
          "--dx": `${p.dx}px`,
          "--dy": `${p.dy}px`,
          "--rot": `${p.rot}deg`,
          "--w": `${p.w}px`,
          "--h": `${p.h}px`,
          "--c": p.c,
          "--delay": `${p.delay}s`,
        }}
      />
    ))}
  </div>
);

const cryingSmileyDrops = [
  { left: "6%", delay: "0s", duration: "2.2s", size: "1.8rem", tilt: "-22deg" },
  { left: "18%", delay: "0.25s", duration: "2.6s", size: "1.4rem", tilt: "14deg" },
  { left: "30%", delay: "0.08s", duration: "2.4s", size: "2rem", tilt: "-10deg" },
  { left: "44%", delay: "0.4s", duration: "2.1s", size: "1.5rem", tilt: "20deg" },
  { left: "56%", delay: "0.15s", duration: "2.55s", size: "1.7rem", tilt: "-18deg" },
  { left: "70%", delay: "0.32s", duration: "2.3s", size: "1.4rem", tilt: "8deg" },
  { left: "82%", delay: "0.06s", duration: "2.45s", size: "1.9rem", tilt: "-14deg" },
  { left: "92%", delay: "0.22s", duration: "2.2s", size: "1.5rem", tilt: "16deg" },
];

const GameField = ({ assignments, assignment, updateAssignment, unlockAssignment, index }) => {
  const [checked, setChecked] = useState(true);
  const [showPasswordPopup, setShowPasswordPopup] = useState(false);
  const [passwordInput, setPasswordInput] = useState("");
  const [passwordError, setPasswordError] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [cryingKey, setCryingKey] = useState(0);
  const [showCrying, setShowCrying] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    setChecked(assignment.checked);
  }, [assignment]);

  function handleClick() {
    if (assignment.type === "password" && !assignment.unlocked) {
      setShowPasswordPopup(true);
      setPasswordInput("");
      setPasswordError(false);
      return;
    }

    setChecked(!checked);
    updateAssignment(checked, assignment.index);
  }

  function handlePasswordSubmit(e) {
    e.preventDefault();
    if (passwordInput.trim().toLowerCase() === assignment.password?.trim().toLowerCase()) {
      setPasswordError(false);
      setShowSuccess(true);
      setTimeout(() => {
        setShowSuccess(false);
        setShowPasswordPopup(false);
        unlockAssignment(assignment.index);
        // Auto-flip the tile
        setChecked(false);
        updateAssignment(true, assignment.index);
      }, 2200);
    } else {
      setPasswordError(true);
      setPasswordInput("");
      setCryingKey((k) => k + 1);
      setShowCrying(true);
      setTimeout(() => setPasswordError(false), 600);
      setTimeout(() => setShowCrying(false), 3500);
    }
  }

  function triggerAnimation(delay) {
    setChecked(true);

    const initialDelay = delay;
    const delayAfterInitial = assignment.flipIndex * 300;

    const initialTimeoutId = setTimeout(() => {
      const timeoutId = setTimeout(() => {
        setChecked(assignment.checked);
      }, delayAfterInitial);

      return () => clearTimeout(timeoutId);
    }, initialDelay);

    return () => clearTimeout(initialTimeoutId);
  }

  useEffect(() => {
    triggerAnimation(300);
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (showPasswordPopup && inputRef.current) {
      inputRef.current.focus();
    }
  }, [showPasswordPopup]);

  const [divWidth, setDivWidth] = useState(0);
  const divRef = useRef(null);

  useEffect(() => {
    const updateWidth = () => {
      if (divRef.current) {
        const width = divRef.current.getBoundingClientRect().width;
        setDivWidth(width);
      }
    };

    window.addEventListener("resize", updateWidth);

    updateWidth();

    return () => {
      window.removeEventListener("resize", updateWidth);
    };
  }, []);

  return (
    <div className="col-4 square">
      <div className="content  p-2">
        <div className="flip-card" ref={divRef}>
          <div
            className={
              checked ? "flip-card-inner" : "flip-card-now flip-card-inner"
            }
            onClick={handleClick}
          >
            <div className="flip-card-front align-items-center d-flex">
              <img
                src={"/bingoCircle.png?v=3"}
                alt="circle"
                className="smallBingoCircle"
              />
            </div>
            <div
              className={
                assignment.type === "insta"
                  ? "flip-card-back d-flex align-items-center justify-content-center instagram"
                  : "flip-card-back d-flex align-items-center justify-content-center"
              }
            >
              {assignment.type === "password" && (
                <TreasureChestIcon sparkleDelay={(assignment.index * 0.83) % 3} unlocked={assignment.unlocked} />
              )}
              <div
                className={
                  assignment.type === "insta"
                    ? "instagram-whitebox d-flex align-items-center justify-content-center"
                    : "d-flex align-items-center justify-content-center"
                }
              >
                <div
                  className="px-2"
                  style={
                    assignment.type === "insta" ? { marginTop: "-22px" } : {}
                  }
                >
                  <p
                    className={`assignmentText m-0 ${
                      checked === false ? "" : "white-text"
                    }`}
                    style={{ fontSize: divWidth * 0.12 }}
                  >
                    {assignment.text}
                  </p>
                </div>
              </div>
              {assignment.type === "insta" && (
                <SocialIconsRow className="social-icons-row" />
              )}
            </div>
          </div>
        </div>
      </div>

      {showPasswordPopup && (
        <div className="password-overlay" onClick={() => !showSuccess && setShowPasswordPopup(false)}>
        <div className="password-card-pop" onClick={(e) => e.stopPropagation()}>
          <div className={`password-card ${passwordError ? "chest-shake" : ""} ${showSuccess ? "password-card-success" : ""}`}>
            {showCrying && (
              <div className="crying-smileys" aria-hidden="true" key={cryingKey}>
                {cryingSmileyDrops.map((drop, i) => (
                  <img
                    key={i}
                    src="/twemoji-crying.svg"
                    alt=""
                    className="crying-smiley"
                    style={{
                      left: drop.left,
                      animationDelay: drop.delay,
                      animationDuration: drop.duration,
                      width: drop.size,
                      height: drop.size,
                      "--tilt": drop.tilt,
                    }}
                  />
                ))}
              </div>
            )}
            <div className="password-chest-header">
              <svg viewBox="0 0 64 48" className="password-key-icon" xmlns="http://www.w3.org/2000/svg">
                <circle cx="18" cy="24" r="12" fill="none" stroke="#fbed65" strokeWidth="3"/>
                <line x1="30" y1="24" x2="58" y2="24" stroke="#fbed65" strokeWidth="3" strokeLinecap="round"/>
                <line x1="48" y1="24" x2="48" y2="32" stroke="#fbed65" strokeWidth="3" strokeLinecap="round"/>
                <line x1="54" y1="24" x2="54" y2="30" stroke="#fbed65" strokeWidth="3" strokeLinecap="round"/>
              </svg>
            </div>
            <p className={`password-title ${showSuccess ? "password-title-success" : ""}`}>
              {showSuccess ? "CORRECT!" : "Wat is het geheime woord?"}
            </p>
            <p className="password-subtitle">Juiste wachtwoord = tegel ontgrendelen</p>
            <form onSubmit={handlePasswordSubmit}>
              <div className="password-input-wrapper">
                <input
                  ref={inputRef}
                  type="text"
                  className={`password-input ${passwordError ? "input-shake" : ""}`}
                  value={passwordInput}
                  onChange={(e) => setPasswordInput(e.target.value)}
                  placeholder="Voer wachtwoord in..."
                  disabled={showSuccess}
                />
                {showSuccess && (
                  <div className="input-success-check" aria-hidden="true">
                    <span className="input-check-burst" />
                    <span className="input-check-burst input-check-burst-2" />
                    <svg className="input-check-icon" viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="16" cy="16" r="14" fill="#2ecc71"/>
                      <path d="M9 16.5 L14 21.5 L23 11" fill="none" stroke="white" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                )}
              </div>
              <p className={`password-error-text ${showCrying ? "visible" : ""}`}>
                Arr! Verkeerd wachtwoord!
              </p>
              <button type="submit" className="password-btn" disabled={showSuccess}>
                Ontgrendel
              </button>
            </form>
            {showSuccess && (
              <>
                {renderPopper(popperLeftPieces, "popper popper-left")}
                {renderPopper(popperRightPieces, "popper popper-right")}
                {renderPopper(popperMidPieces, "popper popper-mid")}
              </>
            )}
          </div>
        </div>
        </div>
      )}
    </div>
  );
};

export default GameField;
